import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { useSharedStyles } from "./sharedStyles";

// ── Tile primitive ────────────────────────────────────────
type TileProps = {
  active: boolean;
  disabled?: boolean;
  title: string;
  hint?: string;
  icon?: React.ReactNode;
  onClick: () => void;
};

const useTileStyles = makeStyles({
  tile: {
    cursor: "pointer",
    appearance: "none",
    textAlign: "left",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
    minHeight: "92px",
    transitionProperty: "border-color, background-color, box-shadow",
    transitionDuration: "150ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      backgroundColor: "#f5eef6",
    },
    ":disabled": { cursor: "not-allowed", opacity: 0.45 },
  },
  tileActive: {
    ...shorthands.border("2px", "dashed", BRAND.purple),
    backgroundColor: "#f5eef6",
    boxShadow: "0 4px 14px rgba(77,32,81,0.18)",
  },
  iconRow: { display: "flex", alignItems: "center", columnGap: "8px" },
  title: { fontWeight: 700, fontSize: "0.98rem", color: BRAND.ink },
  hint: { fontSize: "0.8rem", color: BRAND.muted },
});

const Tile = ({ active, disabled, title, hint, icon, onClick }: TileProps) => {
  const styles = useTileStyles();
  return (
    <button
      type="button"
      disabled={disabled}
      className={mergeClasses(styles.tile, active && styles.tileActive)}
      onClick={onClick}
    >
      <div className={styles.iconRow}>
        {icon}
        <span className={styles.title}>{title}</span>
      </div>
      {hint && <span className={styles.hint}>{hint}</span>}
    </button>
  );
};

// ── ZahlweisePicker ────────────────────────────────────────
const ZAHLWEISE_OPTIONS = [
  { value: "jaehrlich", label: "Jährlich", hint: "1 Rechnung pro Jahr" },
  { value: "halbjaehrlich", label: "Halbjährlich", hint: "2 Rechnungen pro Jahr" },
  { value: "vierteljaehrlich", label: "Vierteljährlich", hint: "4 Rechnungen pro Jahr" },
  { value: "monatlich", label: "Monatlich", hint: "Nur mit Bankeinzug möglich" },
];

const calIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="5" width="18" height="16" rx="2" stroke={BRAND.purple} strokeWidth="1.6" />
    <path d="M3 9h18M8 3v4M16 3v4" stroke={BRAND.purple} strokeWidth="1.6" />
  </svg>
);

export const ZahlweisePicker = ({
  value,
  zahlungsart,
  onChange,
}: {
  value: string;
  zahlungsart: string;
  onChange: (v: string) => void;
}) => {
  const card = useSharedStyles();
  const isAbbuchung = zahlungsart === "abbuchung_courte" || zahlungsart === "abbuchung_versicherer";
  return (
    <div className={card.cardSection}>
      <div className={card.helper}>
        Wählen Sie die gewünschte Zahlweise. <strong>Monatlich</strong> ist nur in Kombination mit
        einer Abbuchungs-Zahlungsart möglich.
      </div>
      <div className={card.tileGrid}>
        {ZAHLWEISE_OPTIONS.map((o) => (
          <Tile
            key={o.value}
            active={value === o.value}
            disabled={o.value === "monatlich" && !isAbbuchung}
            title={o.label}
            hint={o.hint}
            icon={calIcon}
            onClick={() => onChange(o.value)}
          />
        ))}
      </div>
      {value === "monatlich" && !isAbbuchung && (
        <div className={card.helper} style={{ color: "#a1331c" }}>
          Bitte wählen Sie zuerst „Abbuchung durch Courté" oder „Abbuchung durch Versicherer" als
          Zahlungsart, bevor Sie monatliche Zahlweise wählen.
        </div>
      )}
    </div>
  );
};

// ── InkassoartPicker ────────────────────────────────────────
const INKASSO_OPTIONS = [
  { value: "makler", label: "Maklerinkasso vereinbart", hint: "Standard – Courté zieht Beiträge zentral ein." },
  { value: "zentral", label: "Zentralinkasso vereinbart", hint: "Versicherer übernimmt das Inkasso direkt." },
];

export const InkassoartPicker = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) => {
  const card = useSharedStyles();
  return (
    <div className={card.tileGrid2}>
      {INKASSO_OPTIONS.map((o) => (
        <Tile
          key={o.value}
          active={value === o.value}
          title={o.label}
          hint={o.hint}
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="6" width="18" height="12" rx="2" stroke={BRAND.purple} strokeWidth="1.6" />
              <path d="M3 10h18" stroke={BRAND.purple} strokeWidth="1.6" />
              <circle cx="17" cy="14" r="1.5" fill={BRAND.gold} />
            </svg>
          }
          onClick={() => onChange(o.value)}
        />
      ))}
    </div>
  );
};

// ── ZahlungsartPicker ────────────────────────────────────────
export const ZAHLUNGSART_OPTIONS = [
  { value: "ueberweisung_courte", label: "Überweisung an Courté", hint: "Kunde überweist an Courté.", paymentMethodID: 1 },
  { value: "abbuchung_courte", label: "Abbuchung durch Courté", hint: "SEPA-Lastschrift durch Courté.", paymentMethodID: 2 },
  { value: "abbuchung_versicherer", label: "Abbuchung durch Versicherer", hint: "SEPA-Lastschrift direkt durch Generali.", paymentMethodID: 5 },
  { value: "ueberweisung_versicherer", label: "Überweisung an Versicherer", hint: "Kunde überweist direkt an Generali.", paymentMethodID: 4 },
];

export const ZahlungsartPicker = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) => {
  const card = useSharedStyles();
  return (
    <div className={card.tileGrid}>
      {ZAHLUNGSART_OPTIONS.map((o) => (
        <Tile
          key={o.value}
          active={value === o.value}
          title={o.label}
          hint={o.hint}
          icon={
            o.value.startsWith("abbuchung") ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 4v12M6 12l6 6 6-6" stroke={BRAND.purple} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <line x1="4" y1="20" x2="20" y2="20" stroke={BRAND.purple} strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 20V8M6 12l6-6 6 6" stroke={BRAND.purple} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <line x1="4" y1="20" x2="20" y2="20" stroke={BRAND.purple} strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            )
          }
          onClick={() => onChange(o.value)}
        />
      ))}
    </div>
  );
};

// ── BankverbindungBlock ────────────────────────────────────────
export const BankverbindungBlock = ({
  iban,
  bic,
  bankName,
  kontoinhaber,
  onChange,
}: {
  iban: string;
  bic: string;
  bankName: string;
  kontoinhaber: string;
  onChange: (k: "iban" | "bic" | "bankName" | "kontoinhaber", v: string) => void;
}) => {
  const card = useSharedStyles();
  return (
    <div className={card.card}>
      <div className={card.field}>
        <label className={card.label}>IBAN</label>
        <input
          type="text"
          className={card.input}
          value={iban}
          onChange={(e) => onChange("iban", e.target.value.toUpperCase())}
          placeholder="DE00 0000 0000 0000 0000 00"
          maxLength={34}
        />
      </div>
      <div className={card.twoCol}>
        <div className={card.field}>
          <label className={card.label}>BIC</label>
          <input
            type="text"
            className={card.input}
            value={bic}
            onChange={(e) => onChange("bic", e.target.value.toUpperCase())}
            placeholder="z. B. COBADEFFXXX"
            maxLength={11}
          />
        </div>
        <div className={card.field}>
          <label className={card.label}>Bankname</label>
          <input
            type="text"
            className={card.input}
            value={bankName}
            onChange={(e) => onChange("bankName", e.target.value)}
            placeholder="z. B. Commerzbank"
          />
        </div>
      </div>
      <div className={card.field}>
        <label className={card.label}>Kontoinhaber</label>
        <input
          type="text"
          className={card.input}
          value={kontoinhaber}
          onChange={(e) => onChange("kontoinhaber", e.target.value)}
          placeholder="Vor- und Nachname / Firmenname"
        />
      </div>
    </div>
  );
};

// ── RechnungsstellungPicker ────────────────────────────────────────
export const RechnungsstellungPicker = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) => {
  const card = useSharedStyles();
  return (
    <div className={card.cardSection}>
      <div className={card.helper}>
        Diese Auswahl ist nur relevant, wenn die Zahlung an Courté geht.
      </div>
      <div className={card.tileGrid2}>
        <Tile
          active={value === "einzel"}
          title="Einzelrechnung"
          hint="Eine Rechnung pro Vertrag."
          onClick={() => onChange("einzel")}
        />
        <Tile
          active={value === "sammel"}
          title="Sammelrechnung"
          hint="Mehrere Verträge auf einer Rechnung."
          onClick={() => onChange("sammel")}
        />
      </div>
    </div>
  );
};
